import type { JSX } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Alert, App, Empty, Input, Skeleton } from 'antd';
import { BugOutlined, BulbOutlined, BulbTwoTone, ExportOutlined, MessageOutlined, QuestionCircleOutlined, SendOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Button, Chip, PageTitle, Panel, Tabs, Tag, type TagColor } from '../../components/ds';
import { authApi } from '../../lib/authApi';
import { hubApi, type HubTicketDto } from '../../lib/api/hub';
import { questionsApi, wikiApi, type WikiQuestionDto } from '../../lib/wikiApi';
import '../analytics/analytics.css';

const CRM_FEEDBACK_URL = 'https://crm.brasol.ru/appeals/feedback';

type QStatus = 'OPEN' | 'ANSWERED' | 'ALL';
const Q_STATUS: Record<string, { label: string; color: TagColor }> = {
  OPEN: { label: 'Новый', color: 'orange' },
  ANSWERED: { label: 'Отвечен', color: 'green' },
  ARCHIVED: { label: 'В архиве', color: 'default' },
};
const KIND_META: Record<HubTicketDto['kind'], { label: string; icon: JSX.Element; color: TagColor }> = {
  bug: { label: 'Ошибка', icon: <BugOutlined />, color: 'red' },
  idea: { label: 'Улучшение', icon: <BulbOutlined />, color: 'gold' },
  question: { label: 'Обращение', icon: <MessageOutlined />, color: 'blue' },
};

const fmt = (iso: string): string => new Date(iso).toLocaleString('ru-RU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

/** Сколько ждёт ответа: «2 ч», «3 дн.» — для SLA-подписи у новых. */
function waiting(iso: string): string {
  const h = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 3_600_000));
  return h < 24 ? `${h} ч` : `${Math.round(h / 24)} дн.`;
}

/**
 * «Обращения и вопросы» (/admin/questions, HUB 2.0, волна W5). Вкладки:
 *  - «Вопросы по базе» — инбокс WikiQuestion (перенесён из
 *    QuestionsPages.QuestionInboxPage): мастер–деталь, ответ, «В архив»,
 *    подсказка «похожая статья» по поиску — вставить ссылку в ответ;
 *  - «Обращения HUB» — ошибки/идеи/обращения из кнопки «Создать» (CRM
 *    FeedbackTicket с разделом «HUB · …», эндпоинт W1 /wiki/hub/feedback),
 *    ТОЛЬКО чтение: статусы и ответы ведутся на доске CRM → Обращения.
 * Доступ к вопросам — kb:admin; к обращениям — право CRM appeals_feedback:view
 * (без него вкладка честно говорит, что прав нет).
 */
export function InboxPage(): JSX.Element {
  const [sp, setSp] = useSearchParams();
  const tab = sp.get('tab') === 'hub' ? 'hub' : 'questions';
  const me = useQuery({ queryKey: ['wiki-auth', 'me'], queryFn: () => authApi.me(), retry: false, staleTime: 60_000 });
  const canAdmin = me.data?.canAdmin ?? false;
  const stats = useQuery({ queryKey: ['wiki', 'questions', 'stats'], queryFn: () => questionsApi.stats(), enabled: canAdmin, retry: false });
  const hub = useQuery({ queryKey: ['wiki', 'hub', 'feedback', 'open'], queryFn: () => hubApi.feedback('open'), enabled: canAdmin, retry: false });

  if (me.isLoading) return <Skeleton active paragraph={{ rows: 6 }} />;
  if (!canAdmin) {
    return <Alert type="warning" showIcon message="Недостаточно прав" description="Обращения и вопросы доступны администраторам базы знаний (kb:admin)." />;
  }
  return (
    <div className="fk-adm-page">
      <PageTitle
        title="Обращения и вопросы"
        sub="Вопросы по базе знаний и обращения из кнопки «Создать» в HUB. Обращения ведутся на доске CRM → Обращения."
        style={{ marginBottom: 0 }}
        right={
          <Button variant="ghost" href={CRM_FEEDBACK_URL} iconRight={<ExportOutlined />}>
            CRM → Обращения
          </Button>
        }
      />
      <Tabs
        items={[
          { key: 'questions', label: 'Вопросы по базе', icon: <QuestionCircleOutlined />, badge: stats.data?.open || null, badgeColor: 'var(--fk-orange)' },
          { key: 'hub', label: 'Обращения HUB', icon: <MessageOutlined />, badge: hub.data?.length || null },
        ]}
        value={tab}
        onChange={(k) => {
          const next = new URLSearchParams(sp);
          next.set('tab', k);
          setSp(next, { replace: true });
        }}
      />
      {tab === 'questions' ? <QuestionsInbox /> : <HubTickets />}
    </div>
  );
}

function QuestionsInbox(): JSX.Element {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const [filter, setFilter] = useState<QStatus>('OPEN');
  const [sel, setSel] = useState<string | null>(null);
  const inbox = useQuery({ queryKey: ['wiki', 'questions', 'inbox', filter], queryFn: () => questionsApi.inbox(filter), retry: false });
  const rows = inbox.data ?? [];
  const current = rows.find((q) => q.id === sel) ?? rows[0] ?? null;
  useEffect(() => {
    if (current && current.id !== sel) setSel(current.id);
  }, [current, sel]);

  const invalidate = async (): Promise<void> => {
    await qc.invalidateQueries({ queryKey: ['wiki', 'questions'] });
  };
  const answer = useMutation({
    mutationFn: ({ id, text }: { id: string; text: string }) => questionsApi.answer(id, text),
    onSuccess: async () => {
      void message.success('Ответ отправлен');
      await invalidate();
    },
    onError: (e: Error) => void message.error(e.message),
  });
  const archive = useMutation({
    mutationFn: (id: string) => questionsApi.archive(id),
    onSuccess: async () => {
      void message.success('В архиве');
      setSel(null);
      await invalidate();
    },
    onError: (e: Error) => void message.error(e.message),
  });

  return (
    <>
      <div className="fk-adm-hint">
        {(
          [
            ['OPEN', 'Новые'],
            ['ANSWERED', 'Отвеченные'],
            ['ALL', 'Все'],
          ] as const
        ).map(([v, l]) => (
          <Chip key={v} active={filter === v} onClick={() => setFilter(v)}>
            {l}
          </Chip>
        ))}
      </div>
      {inbox.isLoading ? (
        <Skeleton active paragraph={{ rows: 6 }} />
      ) : inbox.error ? (
        <Alert type="error" showIcon message="Не удалось загрузить вопросы" description={(inbox.error as Error).message} />
      ) : rows.length === 0 ? (
        <Panel>
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={filter === 'OPEN' ? 'Новых вопросов нет' : 'Вопросов нет'} />
        </Panel>
      ) : (
        <div className="fk-adm-md">
          <Panel pad={0}>
            <div className="fk-adm-md-list" role="list">
              {rows.map((q) => {
                const st = Q_STATUS[q.status] ?? Q_STATUS.OPEN;
                return (
                  <button key={q.id} type="button" role="listitem" className="fk-adm-md-item" aria-current={current?.id === q.id} onClick={() => setSel(q.id)}>
                    <span style={{ display: 'flex', gap: 8, justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <span className="fk-adm-md-title" style={{ minWidth: 0 }}>{q.question}</span>
                      <Tag color={st.color}>{st.label}</Tag>
                    </span>
                    <span className="fk-adm-md-sub">
                      {q.askedByName ?? 'Аноним'} · {fmt(q.createdAt)}
                      {q.status === 'OPEN' && <span style={{ color: 'var(--fk-warning-text)' }}>· ждёт {waiting(q.createdAt)}</span>}
                    </span>
                  </button>
                );
              })}
            </div>
          </Panel>
          {current && (
            <QuestionDetail
              key={current.id}
              q={current}
              busy={answer.isPending || archive.isPending}
              onAnswer={(text) => answer.mutate({ id: current.id, text })}
              onArchive={() => archive.mutate(current.id)}
            />
          )}
        </div>
      )}
    </>
  );
}

function QuestionDetail({ q, busy, onAnswer, onArchive }: { q: WikiQuestionDto; busy: boolean; onAnswer: (t: string) => void; onArchive: () => void }): JSX.Element {
  const [text, setText] = useState(q.answer ?? '');
  // «Похожая статья»: подсказка поиска по тексту вопроса (без AI — обычный suggest с ACL).
  const suggest = useQuery({
    queryKey: ['wiki', 'questions', 'suggest', q.id],
    queryFn: () => wikiApi.searchSuggest(q.question.slice(0, 120)),
    retry: false,
    staleTime: 5 * 60_000,
  });
  const similar = useMemo(() => (suggest.data?.items ?? []).filter((a) => a.status === 'PUBLISHED').slice(0, 3), [suggest.data]);
  const st = Q_STATUS[q.status] ?? Q_STATUS.OPEN;
  return (
    <Panel title="Ответ · вопрос по базе" right={<Tag color={st.color}>{st.label}</Tag>}>
      <div style={{ fontWeight: 700, fontSize: 16, lineHeight: 1.4 }}>{q.question}</div>
      <div className="fk-adm-note" style={{ marginTop: 4 }}>
        {q.askedByName ?? 'Аноним'} · {fmt(q.createdAt)}
        {q.answeredAt && ` · ответил(а) ${q.answeredByName ?? '—'} ${fmt(q.answeredAt)}`}
      </div>
      {similar.length > 0 && (
        <div className="fk-adm-callout" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {similar.map((a) => (
            <span key={a.id} style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              <BulbTwoTone twoToneColor="#FF6900" />
              Похожая статья: <Link to={`/article/${a.slug}`}>{a.title}</Link> —
              <button
                type="button"
                className="fk-ds-btn fk-ds-btn--text fk-ds-btn--sm"
                style={{ minHeight: 0, padding: '0 4px' }}
                onClick={() => setText((t) => `${t}${t.trim() ? '\n\n' : ''}Ответ есть в статье «${a.title}»: ${window.location.origin}/article/${a.slug}`)}
              >
                вставить ссылку
              </button>
            </span>
          ))}
        </div>
      )}
      <div className="fk-adm-section-label">Ответ партнёру</div>
      <Input.TextArea value={text} onChange={(e) => setText(e.target.value)} placeholder="Ответ партнёру…" autoSize={{ minRows: 4, maxRows: 12 }} maxLength={5000} />
      <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
        <Button variant="primary" icon={<SendOutlined />} disabled={text.trim().length < 2} loading={busy} onClick={() => onAnswer(text)}>
          {q.status === 'ANSWERED' ? 'Обновить ответ' : 'Ответить'}
        </Button>
        {q.status !== 'ARCHIVED' && (
          <Button variant="ghost" disabled={busy} onClick={onArchive}>
            В архив
          </Button>
        )}
        <span className="fk-adm-note" style={{ marginLeft: 'auto', alignSelf: 'center' }}>Частый вопрос — повод дописать статью или FAQ</span>
      </div>
    </Panel>
  );
}

function HubTickets(): JSX.Element {
  const [status, setStatus] = useState<'open' | 'all'>('open');
  const [kind, setKind] = useState<'all' | HubTicketDto['kind']>('all');
  const list = useQuery({ queryKey: ['wiki', 'hub', 'feedback', status], queryFn: () => hubApi.feedback(status), retry: false });
  const rows = (list.data ?? []).filter((t) => kind === 'all' || t.kind === kind);
  const status403 = (list.error as { status?: number } | null)?.status === 403;

  return (
    <>
      <div className="fk-adm-hint">
        <Chip active={kind === 'all'} onClick={() => setKind('all')}>Все</Chip>
        {(Object.keys(KIND_META) as HubTicketDto['kind'][]).map((k) => (
          <Chip key={k} active={kind === k} icon={KIND_META[k].icon} onClick={() => setKind(k)}>
            {KIND_META[k].label}
          </Chip>
        ))}
        <span style={{ flex: 1 }} />
        <Chip active={status === 'open'} onClick={() => setStatus('open')}>В работе</Chip>
        <Chip active={status === 'all'} onClick={() => setStatus('all')}>Все</Chip>
      </div>
      {list.isLoading ? (
        <Skeleton active paragraph={{ rows: 6 }} />
      ) : list.error ? (
        <Alert
          type={status403 ? 'info' : 'error'}
          showIcon
          message={status403 ? 'Нет права «Обращения» в CRM' : 'Не удалось загрузить обращения'}
          description={status403 ? 'Обращения из HUB видят сотрудники с правом CRM «Обращения — просмотр». Попросите выдать его в CRM → Роли.' : (list.error as Error).message}
        />
      ) : rows.length === 0 ? (
        <Panel>
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Обращений нет" />
        </Panel>
      ) : (
        <Panel pad={0} sub="только просмотр — статус и ответ меняются на доске CRM">
          <div className="fk-adm-list">
            {rows.map((t) => {
              const k = KIND_META[t.kind];
              return (
                <a
                  key={t.id}
                  href={`${CRM_FEEDBACK_URL}/${encodeURIComponent(t.id)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="fk-adm-list-row"
                  style={{ gridTemplateColumns: 'minmax(0,2.4fr) minmax(0,1fr) auto auto' }}
                >
                  <span style={{ minWidth: 0 }}>
                    <span style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                      <Tag color={k.color} icon={k.icon}>{t.kindLabel || k.label}</Tag>
                      {t.severity && <Tag color="red">{t.severity}</Tag>}
                      <span className="fk-adm-note">{t.code}</span>
                    </span>
                    <span className="fk-adm-ellipsis" style={{ display: 'block', fontWeight: 600, marginTop: 4 }}>{t.title}</span>
                    <span className="fk-adm-note">
                      {t.authorName ?? '—'}
                      {t.pointName ? ` · ${t.pointName}` : ''}
                      {t.section ? ` · ${t.section}` : ''}
                    </span>
                  </span>
                  <span className="fk-adm-note">{fmt(t.createdAt)}</span>
                  <Tag>{t.statusLabel}</Tag>
                  <ExportOutlined style={{ color: 'var(--fk-text-muted)' }} />
                </a>
              );
            })}
          </div>
        </Panel>
      )}
    </>
  );
}
